import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  Download,
  RefreshCw,
  AlertTriangle,
  Info,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Card from '../../components/common/Card';

const SystemLogs = () => {
  const { logs, loading, fetchLogs } = useAdmin();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ totalPages: 0, totalItems: 0, startItem: 0, endItem: 0 });
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const timerRef = useRef(null);

  const pageSize = 50;

  const loadLogs = useCallback(async (page = currentPage, keepPage = true) => {
    // If called directly from an onClick, the first arg will be a React synthetic event
    if (page && typeof page === 'object' && 'preventDefault' in page) {
      page = currentPage; // fall back to current page
    }

    // Build filters without pushing undefined values (avoid search=undefined etc.)
    const filters = {};
    const trimmedSearch = (searchTerm || '').trim();
    if (trimmedSearch.length > 0) filters.search = trimmedSearch;
    if (levelFilter !== 'all') filters.level = levelFilter;
    if (typeFilter !== 'all') filters.type = typeFilter;

    const safePage = Number(page) || 1;
    const result = await fetchLogs(safePage, pageSize, filters);
    if (result?.pagination) setPagination(result.pagination);
    if (!keepPage) setCurrentPage(1);
  }, [currentPage, levelFilter, typeFilter, searchTerm, fetchLogs]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  useEffect(() => {
    if (!autoRefresh) return;
    timerRef.current = setInterval(() => { loadLogs(currentPage); }, 10000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, currentPage, loadLogs]);

  const handleSearch = (e) => { setSearchTerm(e.target.value); setCurrentPage(1); };

  const handleLevelFilter = (level) => { setLevelFilter(level); setCurrentPage(1); };

  const handleTypeFilter = (type) => { setTypeFilter(type); setCurrentPage(1); };

  const openDetails = (log) => { setSelectedLog(log); setDetailsOpen(true); };
  const closeDetails = () => { setDetailsOpen(false); setSelectedLog(null); };

  const exportCsv = () => {
    const header = ['id','timestamp','level','type','message','userEmail'];
    const lines = [header.join(',')].concat((logs||[]).map(l => [l.id,l.timestamp,l.level,l.type,JSON.stringify(l.message),l.userEmail||''].map(v => `"${String(v||'').replace(/"/g,'""')}"`).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href=url; a.download='system-logs.csv'; a.click(); URL.revokeObjectURL(url);
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(logs||[], null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href=url; a.download='system-logs.json'; a.click(); URL.revokeObjectURL(url);
  };

  const getLevelIcon = (level) => {
    switch (level) {
      case 'ERROR': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'WARN': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'INFO': return <Info className="w-4 h-4 text-blue-400" />;
      case 'DEBUG': return <CheckCircle className="w-4 h-4 text-green-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-400';
      case 'WARN': return 'text-yellow-400';
      case 'INFO': return 'text-blue-400';
      case 'DEBUG': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  if (loading && !logs.length) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#a15507] mx-auto mb-4"></div>
          <p className="text-gray-300">{t('systemLogs.loading', 'Loading system logs...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1f2937] to-[#111827] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">{t('systemLogs.title','System Logs')}</h1>
            <p className="text-gray-400 text-sm md:text-base">{t('systemLogs.subtitle','Monitor system activity and debug information')}</p>
          </div>
          <div className="flex gap-3 w-full sm:w-auto">
            <Button variant="secondary" size="sm" onClick={exportCsv}><Download className="w-4 h-4 mr-2" />CSV</Button>
            <Button variant="secondary" size="sm" onClick={exportJson}><Download className="w-4 h-4 mr-2" />JSON</Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => loadLogs(currentPage, true)}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('systemLogs.refresh','Refresh')}
            </Button>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder={t('systemLogs.searchPlaceholder','Search logs...')}
                value={searchTerm}
                onChange={handleSearch}
                size="sm"
                className="pl-10"
              />
            </div>
            
            <Select
              value={levelFilter}
              onChange={(e) => handleLevelFilter(e.target.value)}
              size="sm"
            >
              <option value="all">{t('systemLogs.level.all','All Levels')}</option>
              <option value="ERROR">{t('systemLogs.level.ERROR','Error')}</option>
              <option value="WARN">{t('systemLogs.level.WARN','Warning')}</option>
              <option value="INFO">{t('systemLogs.level.INFO','Info')}</option>
              <option value="DEBUG">{t('systemLogs.level.DEBUG','Debug')}</option>
            </Select>

            <Select
              value={typeFilter}
              onChange={(e) => handleTypeFilter(e.target.value)}
              size="sm"
            >
              <option value="all">{t('systemLogs.type.all','All Types')}</option>
              <option value="AUTH">{t('systemLogs.type.AUTH','Authentication')}</option>
              <option value="PAYMENT">{t('systemLogs.type.PAYMENT','Payment')}</option>
              <option value="SHOP">{t('systemLogs.type.SHOP','Shop')}</option>
              <option value="USER">{t('systemLogs.type.USER','User')}</option>
              <option value="SYSTEM">{t('systemLogs.type.SYSTEM','System')}</option>
              <option value="VISITOR">{t('systemLogs.type.VISITOR','Visitor')}</option>
            </Select>

            <Button
              onClick={() => loadLogs(1, false)}
              size="sm"
              className="w-full md:w-auto"
            >
              <RefreshCw className="w-4 h-4 mr-2" />{t('systemLogs.applyFilters','Apply Filters')}
            </Button>
          </div>
        </Card>

        {/* Logs Table */}
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('systemLogs.columns.level','Level')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('systemLogs.columns.type','Type')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('systemLogs.columns.message','Message')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('systemLogs.columns.user','User')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('systemLogs.columns.timestamp','Timestamp')}</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium text-sm">{t('systemLogs.columns.details','Details')}</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(logs) && logs.length > 0 && logs.map((log) => (
                  <tr key={log.id} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        {getLevelIcon(log.level)}
                        <span className={`ml-2 font-medium text-sm ${getLevelColor(log.level)}`}>
                          {log.level}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-gray-600 text-white text-xs rounded-full">
                        {log.type}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-white text-sm max-w-md truncate">
                        {log.message}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-gray-400 text-sm">
                        {log.userEmail || 'System'}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-gray-400 text-sm">
                        {formatTimestamp(log.timestamp)}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <Button variant="ghost" size="xs" onClick={() => openDetails(log)}>{t('systemLogs.viewDetails','View Details')}</Button>
                    </td>
                  </tr>
                ))}
                {(!loading && Array.isArray(logs) && logs.length === 0) && (
                  <tr>
                    <td colSpan="6" className="py-6 px-4 text-center text-gray-500 text-sm">{t('systemLogs.empty','No logs found')}</td>
                  </tr>
                )}
                {loading && Array.isArray(logs) && logs.length > 0 && (
                  <tr>
                    <td colSpan="6" className="py-3 px-4 text-center text-gray-500 text-sm">{t('systemLogs.loading','Loading system logs...')}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-6 border-t border-gray-700">
              <p className="text-gray-400 text-sm">
                {t('systemLogs.pagination.showing','Showing')} {pagination.startItem} {t('systemLogs.pagination.to','to')} {pagination.endItem} {t('systemLogs.pagination.of','of')} {pagination.totalItems} {t('systemLogs.pagination.items','logs')}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  {t('systemLogs.pagination.prev','Previous')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                >
                  {t('systemLogs.pagination.next','Next')}
                </Button>
              </div>
            </div>
          )}
        </Card>
        <div className="flex items-center mt-4 gap-4">
          <label className="flex items-center text-sm text-gray-400 gap-2 cursor-pointer select-none">
            <input type="checkbox" className="form-checkbox" checked={autoRefresh} onChange={(e)=>setAutoRefresh(e.target.checked)} />
            {t('systemLogs.autoRefresh','Auto refresh (10s)')}
          </label>
          <p className="text-xs text-gray-500">{t('systemLogs.lastUpdated','Last updated')}: {new Date().toLocaleTimeString()}</p>
        </div>

        <LogDetailsModal isOpen={detailsOpen} onClose={closeDetails} log={selectedLog} />
      </div>
    </div>
  );
};

const LogDetailsModal = ({ isOpen, onClose, log }) => {
  const { t } = useTranslation();
  if (!isOpen || !log) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-b from-[#1f2937] to-[#111827] rounded-xl border border-gray-700/50 p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-white">{t('systemLogs.detailsTitle','Log Details')}</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-gray-400 text-xs mb-1">{t('systemLogs.columns.level','Level')}</p>
            <p className="text-white text-sm font-mono">{log.level}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">{t('systemLogs.columns.type','Type')}</p>
            <p className="text-white text-sm font-mono">{log.type}</p>
          </div>
            <div>
            <p className="text-gray-400 text-xs mb-1">{t('systemLogs.columns.message','Message')}</p>
            <p className="text-white text-sm break-words">{log.message}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">{t('systemLogs.columns.user','User')}</p>
            <p className="text-white text-sm font-mono">{log.userEmail || t('systemLogs.system','System')}</p>
          </div>
          <div>
            <p className="text-gray-400 text-xs mb-1">{t('systemLogs.columns.timestamp','Timestamp')}</p>
            <p className="text-white text-sm font-mono">{new Date(log.timestamp).toLocaleString()}</p>
          </div>
          {log.details && (
            <div>
              <p className="text-gray-400 text-xs mb-1">{t('systemLogs.columns.additional','Additional Details')}</p>
              <pre className="bg-gray-900 p-4 rounded-lg text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-all">{JSON.stringify(log.details,null,2)}</pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemLogs;
